// =============================================================================
// themes/shared — partiale wspolne dla motywow: <head>, nawigacja, stopka,
// karty wpisow, hero, okruszki, awatar autora, formatowanie dat, escape.
// Czyste stringi (dziala w przegladarce i w node — zero DOM).
// =============================================================================

import type { PageModel, SiteModel, CategoryModel, AuthorModel } from "../types";
import type { Variation, CardVariant, DateFormat } from "./variation";
import { absoluteUrl, encodePath } from "../urlPolicy";
import { renderNavHtml, renderFooterMenuHtml } from "../menuBuilder";

export interface RenderCtx {
  site: SiteModel;
  v: Variation;
  /** Sciezka arkusza CSS motywu, np. "/assets/tk-a3f.css" */
  cssPath: string;
  /** Dodatkowe tagi do <head> (favicony z brand assets). */
  headExtras: string;
  /** src obrazu -> sciezka lokalna (po re-hostingu). Pusta mapa = zostaw oryginalne URL-e. */
  assetMap: Map<string, string>;
}

export function esc(s: string | null | undefined): string {
  return (s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function escAttr(s: string | null | undefined): string {
  return esc(s);
}

const PL_MONTHS = [
  "stycznia", "lutego", "marca", "kwietnia", "maja", "czerwca",
  "lipca", "sierpnia", "września", "października", "listopada", "grudnia",
];
const PL_MONTHS_SHORT = ["sty", "lut", "mar", "kwi", "maj", "cze", "lip", "sie", "wrz", "paź", "lis", "gru"];

/** Format daty bez Intl — deterministyczny miedzy przegladarka a node. */
export function formatDate(iso: string | null | undefined, fmt: DateFormat, language = "pl"): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const day = d.getUTCDate();
  const m = d.getUTCMonth();
  const y = d.getUTCFullYear();
  const pl = (language || "pl").toLowerCase().startsWith("pl");
  if (fmt === "numeric") return `${String(day).padStart(2, "0")}.${String(m + 1).padStart(2, "0")}.${y}`;
  if (fmt === "short") return pl ? `${day} ${PL_MONTHS_SHORT[m]} ${y}` : `${y}-${String(m + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  return pl ? `${day} ${PL_MONTHS[m]} ${y}` : `${y}-${String(m + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export function isoDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString();
}

/** URL obrazu po re-hostingu (albo oryginal, gdy nie ma w mapie). */
export function imageUrl(ctx: RenderCtx, src: string | null | undefined): string {
  if (!src) return "";
  return ctx.assetMap.get(src) || src;
}

export function readingTime(html: string): number {
  const words = html.replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
}

/** Krotki opis z tresci, gdy brak meta description / excerpt. */
export function excerptFromHtml(html: string, max = 160): string {
  const text = html
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  return `${cut.slice(0, cut.lastIndexOf(" ") > 40 ? cut.lastIndexOf(" ") : max)}…`;
}

/** Strony-smieci z WP, ktorych nie linkujemy w nawigacji/stopce (zostaja pod swoim URL dla parity). */
export function isJunkPage(page: PageModel): boolean {
  return /\/(sample-page|przykladowa-strona|hello-world|witaj-swiecie)\/$/i.test(page.path);
}

export function isPost(page: PageModel): boolean {
  return page.kind === "article" || page.kind === "pillar" || page.kind === "legacy_post";
}

/** Sortowanie od najnowszych (brak daty = na koniec). */
export function sortByDateDesc(pages: PageModel[]): PageModel[] {
  return [...pages].sort((a, b) => {
    const ta = a.datePublished ? new Date(a.datePublished).getTime() : 0;
    const tb = b.datePublished ? new Date(b.datePublished).getTime() : 0;
    return tb - ta;
  });
}

export function categoryFor(site: SiteModel, page: PageModel): CategoryModel | null {
  return page.categorySlug ? site.categories.find((c) => c.slug === page.categorySlug) || null : null;
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");
}

/** Awatar autora: zdjecie albo kolko z inicjalami. */
export function authorAvatar(ctx: RenderCtx, a: AuthorModel, size: "sm" | "lg" = "sm"): string {
  const p = ctx.v.classPrefix;
  const photo = imageUrl(ctx, a.photoUrl);
  if (photo) return `<img class="${p}avatar ${p}avatar-${size}" src="${escAttr(photo)}" alt="${escAttr(a.name)}" loading="lazy">`;
  return `<span class="${p}avatar ${p}avatar-${size} ${p}avatar-txt" aria-hidden="true">${esc(initials(a.name))}</span>`;
}

export interface HeadInput {
  title: string;
  description: string | null;
  canonicalPath: string;
  ogType: "website" | "article";
  ogImage: string | null;
  jsonLd: unknown | null;
  published?: string | null;
  modified?: string | null;
  noindex?: boolean;
}

export function renderHead(ctx: RenderCtx, h: HeadInput): string {
  const { site } = ctx;
  const canonical = absoluteUrl(site.baseUrl, encodePath(h.canonicalPath));
  const lines: string[] = [
    `<meta charset="utf-8">`,
    `<meta name="viewport" content="width=device-width, initial-scale=1">`,
    `<title>${esc(h.title)}</title>`,
  ];
  if (h.description) lines.push(`<meta name="description" content="${escAttr(h.description)}">`);
  if (h.noindex) lines.push(`<meta name="robots" content="noindex, follow">`);
  lines.push(`<link rel="canonical" href="${escAttr(canonical)}">`);
  lines.push(`<meta property="og:type" content="${h.ogType}">`);
  lines.push(`<meta property="og:title" content="${escAttr(h.title)}">`);
  if (h.description) lines.push(`<meta property="og:description" content="${escAttr(h.description)}">`);
  lines.push(`<meta property="og:url" content="${escAttr(canonical)}">`);
  lines.push(`<meta property="og:site_name" content="${escAttr(site.siteName)}">`);
  lines.push(`<meta property="og:locale" content="${escAttr(localeFor(site.language))}">`);
  if (h.ogImage) {
    lines.push(`<meta property="og:image" content="${escAttr(h.ogImage)}">`);
    lines.push(`<meta name="twitter:card" content="summary_large_image">`);
  } else {
    lines.push(`<meta name="twitter:card" content="summary">`);
  }
  if (h.ogType === "article") {
    if (h.published) lines.push(`<meta property="article:published_time" content="${escAttr(isoDate(h.published))}">`);
    if (h.modified) lines.push(`<meta property="article:modified_time" content="${escAttr(isoDate(h.modified))}">`);
  }
  lines.push(`<link rel="alternate" type="application/rss+xml" title="${escAttr(site.siteName)}" href="${escAttr(absoluteUrl(site.baseUrl, "/feed/"))}">`);
  lines.push(`<link rel="stylesheet" href="${escAttr(ctx.cssPath)}">`);
  if (ctx.headExtras) lines.push(ctx.headExtras);
  if (h.jsonLd) {
    lines.push(`<script type="application/ld+json">${JSON.stringify(h.jsonLd).replace(/<\/script/gi, "<\\/script")}</script>`);
  }
  return lines.join("\n");
}

export function localeFor(language: string | null | undefined): string {
  const l = (language || "pl").toLowerCase();
  if (l.startsWith("pl")) return "pl_PL";
  if (l.startsWith("en")) return "en_US";
  if (l.startsWith("de")) return "de_DE";
  return `${l.slice(0, 2)}_${l.slice(0, 2).toUpperCase()}`;
}

export function htmlLang(language: string | null | undefined): string {
  const l = (language || "pl").toLowerCase();
  return l.slice(0, 2) || "pl";
}

export function wrapDocument(ctx: RenderCtx, head: string, body: string, bodyClass: string): string {
  return `<!doctype html>
<html lang="${htmlLang(ctx.site.language)}">
<head>
${head}
</head>
<body class="${bodyClass}">
${body}
</body>
</html>
`;
}

/**
 * Naglowek: logo + menu z SiteModel.menu (WSZYSTKIE kategorie, grupy jako dropdowny,
 * mobile = checkbox-hamburger) — jeden renderer dla trzech motywow (menuBuilder).
 */
export function renderNav(ctx: RenderCtx, current?: string): string {
  const { site, v } = ctx;
  const p = v.classPrefix;
  const logo = site.brand?.logoSvg
    ? `<a class="${p}logo" href="/" aria-label="${escAttr(site.siteName)}"><img src="/logo.svg" alt="" width="36" height="36"><span>${esc(site.siteName)}</span></a>`
    : `<a class="${p}logo" href="/"><span class="${p}logo-mark" aria-hidden="true">${esc(initials(site.siteName).slice(0, 1))}</span><span>${esc(site.siteName)}</span></a>`;
  return `<header class="${p}header ${p}nav-${v.navStyle}">
<div class="${p}wrap ${p}header-in">
${logo}
${renderNavHtml(site.menu, site, { prefix: p, current })}
</div>
</header>`;
}

export function renderFooter(ctx: RenderCtx): string {
  const { site, v } = ctx;
  const p = v.classPrefix;
  const year = new Date().getUTCFullYear();
  const cats = [...site.categories].sort((a, b) => b.count - a.count);
  const pages = site.pages.filter((pg) => (pg.kind === "subpage" || pg.kind === "legacy_page") && !isJunkPage(pg)).slice(0, 8);
  const catCol = cats.length
    ? `<div><h4>Tematy</h4><ul>${cats.slice(0, 12).map((c) => `<li><a href="${escAttr(encodePath(c.path))}">${esc(c.name)}</a></li>`).join("")}</ul></div>`
    : "";
  const pageCol = pages.length || site.author?.path
    ? `<div><h4>Strony</h4><ul>${site.author?.path ? `<li><a href="${escAttr(encodePath(site.author.path))}">${esc(site.author.name)}</a></li>` : ""}${pages.map((pg) => `<li><a href="${escAttr(encodePath(pg.path))}">${esc(pg.title)}</a></li>`).join("")}<li><a href="/feed/">RSS</a></li></ul></div>`
    : "";
  // menu.footer (z panelu) wygrywa nad domyslnymi kolumnami
  const footerMenu = renderFooterMenuHtml(site.menu?.footer, site, p);
  return `<footer class="${p}footer">
<div class="${p}wrap ${p}footer-grid">
<div class="${p}footer-brand">
<a class="${p}logo" href="/">${site.brand?.logoSvg ? `<img src="/logo.svg" alt="" width="36" height="36">` : ""}<span>${esc(site.siteName)}</span></a>
${site.settings.tagline ? `<p>${esc(site.settings.tagline)}</p>` : ""}
</div>
${footerMenu || `${catCol}\n${pageCol}`}
</div>
<div class="${p}wrap ${p}footer-bottom"><span>© ${year} ${esc(site.siteName)}</span></div>
</footer>`;
}

export function renderBreadcrumbs(ctx: RenderCtx, items: Array<{ name: string; path: string | null }>): string {
  const p = ctx.v.classPrefix;
  const parts = items.map((it, i) =>
    it.path && i < items.length - 1
      ? `<li><a href="${escAttr(encodePath(it.path))}">${esc(it.name)}</a></li>`
      : `<li aria-current="page">${esc(it.name)}</li>`,
  );
  return `<nav class="${p}crumbs" aria-label="Okruszki"><ol>${parts.join("")}</ol></nav>`;
}

export function categoryChip(ctx: RenderCtx, page: PageModel): string {
  const p = ctx.v.classPrefix;
  const cat = categoryFor(ctx.site, page);
  if (!cat) return "";
  return `<a class="${p}chip" href="${escAttr(encodePath(cat.path))}">${esc(cat.name)}</a>`;
}

export function postCard(ctx: RenderCtx, page: PageModel, variant: CardVariant): string {
  const { v, site } = ctx;
  const p = v.classPrefix;
  const img = imageUrl(ctx, page.featuredImageUrl);
  const date = formatDate(page.datePublished, v.dateFormat, site.language);
  const desc = page.excerpt || page.metaDescription || excerptFromHtml(page.bodyHtml, 140);
  const imgHtml = img
    ? `<a class="${p}card-img" href="${escAttr(encodePath(page.path))}" tabindex="-1" aria-hidden="true"><img src="${escAttr(img)}" alt="" loading="lazy"></a>`
    : `<a class="${p}card-img ${p}card-img-empty" href="${escAttr(encodePath(page.path))}" tabindex="-1" aria-hidden="true"><span>${esc(initials(page.title))}</span></a>`;
  const body = `<div class="${p}card-body">
${categoryChip(ctx, page)}
<h3><a href="${escAttr(encodePath(page.path))}">${esc(page.title)}</a></h3>
<p>${esc(desc)}</p>
<div class="${p}card-meta">${date ? `<time datetime="${escAttr(isoDate(page.datePublished))}">${esc(date)}</time>` : ""}<span>${readingTime(page.bodyHtml)} min</span></div>
</div>`;
  if (variant === "c") return `<article class="${p}card ${p}card-c">${imgHtml}${body}</article>`;
  return `<article class="${p}card ${p}card-a">${imgHtml}${body}</article>`;
}

/** Duzy wyrozniony wpis (hero na home). */
export function heroCard(ctx: RenderCtx, page: PageModel): string {
  const { v, site } = ctx;
  const p = v.classPrefix;
  const img = imageUrl(ctx, page.featuredImageUrl);
  const date = formatDate(page.datePublished, v.dateFormat, site.language);
  const desc = page.excerpt || page.metaDescription || excerptFromHtml(page.bodyHtml, 220);
  return `<article class="${p}hero-card">
${img ? `<a class="${p}hero-img" href="${escAttr(encodePath(page.path))}" tabindex="-1" aria-hidden="true"><img src="${escAttr(img)}" alt="" fetchpriority="high"></a>` : ""}
<div class="${p}hero-body">
${categoryChip(ctx, page)}
<h2><a href="${escAttr(encodePath(page.path))}">${esc(page.title)}</a></h2>
<p>${esc(desc)}</p>
<div class="${p}card-meta">${site.author ? `<span>${esc(site.author.name)}</span>` : ""}${date ? `<time datetime="${escAttr(isoDate(page.datePublished))}">${esc(date)}</time>` : ""}<span>${readingTime(page.bodyHtml)} min czytania</span></div>
<a class="${p}btn" href="${escAttr(encodePath(page.path))}">Czytaj artykuł</a>
</div>
</article>`;
}

/** Zwarta lista wpisow (tytul + data) — obok hero, "powiazane" w sidebarze. */
export function postList(ctx: RenderCtx, pages: PageModel[]): string {
  const { v, site } = ctx;
  const p = v.classPrefix;
  return `<ol class="${p}list">${pages
    .map((pg) => {
      const img = imageUrl(ctx, pg.featuredImageUrl);
      const date = formatDate(pg.datePublished, v.dateFormat, site.language);
      return `<li>${img ? `<a class="${p}list-img" href="${escAttr(encodePath(pg.path))}" tabindex="-1" aria-hidden="true"><img src="${escAttr(img)}" alt="" loading="lazy"></a>` : ""}<div><a class="${p}list-title" href="${escAttr(encodePath(pg.path))}">${esc(pg.title)}</a>${date ? `<time datetime="${escAttr(isoDate(pg.datePublished))}">${esc(date)}</time>` : ""}</div></li>`;
    })
    .join("")}</ol>`;
}

export function categoryGrid(ctx: RenderCtx, categories: CategoryModel[]): string {
  const p = ctx.v.classPrefix;
  if (!categories.length) return "";
  return `<div class="${p}cat-grid">${categories
    .map((c) => `<a class="${p}cat-card" href="${escAttr(encodePath(c.path))}"><span class="${p}cat-name">${esc(c.name)}</span>${c.description ? `<span class="${p}cat-desc">${esc(c.description)}</span>` : ""}<span class="${p}cat-count">${c.count} ${c.count === 1 ? "artykuł" : c.count < 5 ? "artykuły" : "artykułów"}</span></a>`)
    .join("")}</div>`;
}

export function authorBox(ctx: RenderCtx, a: AuthorModel, opts: { heading?: boolean } = {}): string {
  const p = ctx.v.classPrefix;
  const bio = a.bioHtml ? excerptFromHtml(a.bioHtml, 260) : "";
  return `<aside class="${p}author-box">
${authorAvatar(ctx, a, "lg")}
<div>
${opts.heading ? `<span class="${p}eyebrow">Autor</span>` : ""}
<h3>${a.path ? `<a href="${escAttr(encodePath(a.path))}">${esc(a.name)}</a>` : esc(a.name)}</h3>
${a.jobTitle ? `<p class="${p}author-role">${esc(a.jobTitle)}</p>` : ""}
${bio ? `<p>${esc(bio)}</p>` : ""}
${a.path ? `<a class="${p}link-more" href="${escAttr(encodePath(a.path))}">Więcej o autorze →</a>` : ""}
</div>
</aside>`;
}
